// Work Orders › Canvas: every canvas, sail and cushion piece off its boat, in sections by where it is
// (Stored · Out at loft · Returned · Back on boat this season), one row per piece that opens on a
// click to show its buttons, repairs and history, with the kind pills, a loft filter,
// Overdue only and search. Tick pieces in one section for a batch move: Send out (with a printable
// pickup list) or Mark returned (checking a loft's delivery in). The rows and single-piece moves
// are canvasPieces.js. Live: the board listens to two small queries while it is on screen
// (canvasData.watchBoard), never to every piece.

import { idbCursorAll, escapeHTML as esc, toastMessage } from './utilities';
import { patchHTML, applyLive } from './liveView';
import { database } from './initFirebase';
import { ref, get } from 'firebase/database';
import { GROUPS, STATUSES, buildBoard, groupCounts, isOverdue, pieceName, errorText } from './canvasCore';
import { watchBoard, runBatch, loadLofts, loadSpots, today, emailRepairs } from './canvasData';
import { pieceController, fmtDay } from './canvasPieces';

const COLUMNS = {
  stored: { title: 'Stored', hint: 'Pulled, at the marina, not sent anywhere yet.' },
  out: { title: 'Out at loft', hint: 'At a loft for cleaning or repair.' },
  returned: { title: 'Returned', hint: 'Back from the loft, waiting to go on the boat.' },
  'on-boat': { title: 'Back on boat', hint: 'Put back on this season.' },
};
const SENDABLE = ['stored', 'returned'];

let pieces = [];
let customersById = new Map();
let group = 'all';
let loft = '';
let overdueOnly = false;
const selected = new Set();
let batch = { vendorId: '', dueBack: '', location: '' }; // the batch bar's fields, kept across redraws
let lofts = null;
let spots = [];
let stopWatch = null;
let pending = false; // data arrived while a form was open

const el = id => document.getElementById(id);
const viewShown = () => { const v = el('canvas-view'); return !!v && v.style.display !== 'none'; };

function names(p) {
  const c = customersById.get(p.Customer_ID);
  const boat = c?.Boats?.[p.Boat_ID] || Object.values(c?.Boats || {}).find(b => b && b.Boat_ID === p.Boat_ID);
  const customer = c ? [c.Last_Name, c.First_Name].filter(Boolean).join(', ') : '';
  return { boat: boat?.Boat_Name || '', customer };
}

const ctl = pieceController({
  root: () => el('canvas-view'),
  getPieces: () => pieces,
  reload: async () => {}, // the board's listener brings every change
  redraw: () => draw(),
  names,
  selectable: p => SENDABLE.includes(p.Status) || p.Status === 'out',
  selected,
  onSelect: () => draw(),
  remove: false,
  repairs: true,
  rows: true,
  onEmail: p => emailRepairs(p, { pieces, boatName: names(p).boat }),
  toast: msg => toastMessage(msg, 'blue', 5000),
});

function filtered() {
  const q = (el('canvas-search')?.value || '').trim();
  return buildBoard(pieces, {
    group, vendorId: loft, overdueOnly, search: q, today: today(),
    names: p => { const n = names(p); return `${n.boat} ${n.customer}`; },
  });
}

function barHTML() {
  const counts = groupCounts(pieces); // the board's pieces: off their boats, or put back this season
  const loftsHere = [...new Map(pieces.filter(p => p.Vendor_ID).map(p => [p.Vendor_ID, p.Vendor_Name || p.Vendor_ID])).entries()]
    .sort((a, b) => a[1].localeCompare(b[1]));
  const overdue = pieces.filter(p => isOverdue(p, today())).length;
  return `
    <div class="cv-pills" role="group" aria-label="Show">${GROUPS.map(g => `
      <button type="button" class="cv-pill${g.key === group ? ' cv-pill-on' : ''}" data-key="pill-${g.key}" data-cvb-group="${g.key}" aria-pressed="${g.key === group}">${esc(g.label)} <span class="cv-pill-n">${counts[g.key]}</span></button>`).join('')}
    </div>
    <select class="cvb-loft" id="cvb-loft" aria-label="Loft" data-key="loft">
      <option value="">All lofts</option>
      ${loftsHere.map(([id, name]) => `<option value="${esc(id)}"${id === loft ? ' selected' : ''}>${esc(name)}</option>`).join('')}
    </select>
    <button type="button" class="cv-pill${overdueOnly ? ' cv-pill-on cvb-overdue-on' : ''}" data-key="overdue" id="cvb-overdue" aria-pressed="${overdueOnly}">Overdue only <span class="cv-pill-n">${overdue}</span></button>
    <button type="button" class="cv-btn cvb-print" data-key="print" id="cvb-print-loft"${loft ? '' : ' hidden'}>Print what's at this loft</button>`;
}

function selectedPieces() { return pieces.filter(p => selected.has(p.id)); }

function batchHTML() {
  const list = selectedPieces();
  if (!list.length) return '';
  const statuses = new Set(list.map(p => (p.Status === 'out' ? 'out' : SENDABLE.includes(p.Status) ? 'send' : 'other')));
  const n = list.length;
  const head = `<span class="cvb-batch-n">${n} selected</span>`;
  const tail = `<button type="button" class="cv-btn" data-cvb="print">Print list</button><button type="button" class="cv-link" data-cvb="clear">Clear</button>`;
  if (statuses.size > 1 || statuses.has('other')) {
    return `${head}<span class="cvb-batch-msg">Pick pieces from one section to move them together.</span>${tail}`;
  }
  if (statuses.has('send')) {
    return `${head}
      <label class="cv-field"><span>Send out to</span>
        <select name="vendorId" data-cvb-field>${lofts ? `<option value="">Choose a loft…</option>${lofts.map(v => `<option value="${esc(v.id)}"${v.id === batch.vendorId ? ' selected' : ''}>${esc(v.name)}</option>`).join('')}` : '<option value="">Loading vendors…</option>'}</select>
      </label>
      <label class="cv-field cv-field-narrow"><span>Due back (optional)</span><input type="date" name="dueBack" data-cvb-field value="${esc(batch.dueBack)}"></label>
      <button type="button" class="cv-btn cv-btn-primary" data-cvb="sendOut">Send out ${n} &amp; print pickup list</button>${tail}`;
  }
  return `${head}
    <label class="cv-field"><span>Stored at</span><input type="text" name="location" data-cvb-field list="cvb-spots" maxlength="120" value="${esc(batch.location)}" placeholder="e.g. Canvas loft – incoming" autocomplete="off"></label>
    <button type="button" class="cv-btn cv-btn-primary" data-cvb="return">Mark ${n} returned</button>${tail}
    <datalist id="cvb-spots">${spots.map(s => `<option value="${esc(s)}"></option>`).join('')}</datalist>`;
}

function columnsHTML(cols) {
  return STATUSES.map(s => {
    const list = cols[s.key];
    const c = COLUMNS[s.key];
    const allTicked = list.length && list.every(p => selected.has(p.id));
    return `
      <section class="cvb-col cvb-col--${s.key}" data-key="col-${s.key}">
        <div class="cvb-col-head">
          <span class="cvb-col-title">${esc(c.title)}</span>
          <span class="cvb-col-n">${list.length}</span>
          <span class="cvb-col-hint">${esc(c.hint)}</span>
          ${s.key !== 'on-boat' && list.length > 1 ? `<button type="button" class="cv-link cvb-all" data-cvb-all="${s.key}">${allTicked ? 'Untick all' : 'Tick all'}</button>` : ''}
        </div>
        <div class="cvb-cards">${list.length ? list.map(ctl.cardHTML).join('') : '<div class="cv-empty">Nothing here.</div>'}</div>
      </section>`;
  }).join('');
}

function draw() {
  const board = el('cvb-columns');
  if (!board) return;
  const cols = filtered();
  patchHTML(el('cvb-bar'), barHTML());
  const batchEl = el('cvb-batch');
  patchHTML(batchEl, batchHTML());
  batchEl.hidden = !selected.size;
  patchHTML(board, `${columnsHTML(cols)}${ctl.datalistHTML()}`);
  wire();
}

function wire() {
  el('cvb-bar').querySelectorAll('[data-cvb-group]').forEach(b => { b.onclick = () => { group = b.dataset.cvbGroup; draw(); }; });
  el('cvb-loft').onchange = e => { loft = e.target.value; draw(); };
  el('cvb-overdue').onclick = () => { overdueOnly = !overdueOnly; draw(); };
  el('cvb-print-loft').onclick = () => printList(`At ${pieces.find(p => p.Vendor_ID === loft)?.Vendor_Name || 'the loft'}`,
    pieces.filter(p => p.Status === 'out' && p.Vendor_ID === loft), 'return');
  el('cvb-columns').querySelectorAll('[data-cvb-all]').forEach(b => { b.onclick = () => {
    const list = filtered()[b.dataset.cvbAll];
    const all = list.every(p => selected.has(p.id));
    list.forEach(p => (all ? selected.delete(p.id) : selected.add(p.id)));
    draw();
  }; });
  const bar = el('cvb-batch');
  bar.querySelectorAll('[data-cvb-field]').forEach(f => { f.oninput = f.onchange = () => { batch[f.name] = f.value; }; });
  bar.querySelectorAll('[data-cvb]').forEach(b => { b.onclick = () => batchAction(b.dataset.cvb); });
  ctl.wire();
  // Loft list and spot suggestions, once something needs them.
  const list = selectedPieces();
  if (list.length && !lofts && list.every(p => SENDABLE.includes(p.Status))) loadLofts().then(v => { lofts = v; draw(); }).catch(() => {});
  if (list.length && !spots.length && list.every(p => p.Status === 'out')) loadSpots().then(s => { if (s.length) { spots = s; draw(); } }).catch(() => {});
}

async function batchAction(kind) {
  const list = selectedPieces();
  if (kind === 'clear') { selected.clear(); draw(); return; }
  if (kind === 'print') { printList('Canvas & sails', list, list.every(p => p.Status === 'out') ? 'return' : 'pickup'); return; }
  const input = kind === 'sendOut'
    ? { vendorId: batch.vendorId, vendorName: (lofts || []).find(v => v.id === batch.vendorId)?.name || '', dueBack: batch.dueBack }
    : { location: batch.location };
  // The print window opens inside the click (a pop-up opened after an await is blocked).
  const win = kind === 'sendOut' && input.vendorId ? window.open('', '_blank') : null;
  try {
    const { moved, refused } = await runBatch(list, kind, input);
    if (refused.length && !moved.length) throw refused[0].err;
    moved.forEach(p => selected.delete(p.id));
    if (refused.length) toastMessage(`${refused.length} not moved: ${errorText(refused[0].err)}`, 'red');
    else toastMessage(kind === 'sendOut' ? `${moved.length} sent to ${input.vendorName}` : `${moved.length} marked returned`, 'green');
    // The pickup list says where each piece was before it left (the move clears its spot).
    if (kind === 'sendOut') printList(`Pickup for ${input.vendorName}`, list.filter(p => moved.some(m => m.id === p.id)), 'pickup', win);
    batch = { vendorId: batch.vendorId, dueBack: '', location: '' };
  } catch (err) {
    win?.close();
    toastMessage(errorText(err) || 'Couldn\'t save', 'red');
    if (!err.canvasCode) console.error('Canvas board: batch failed', err);
  }
  draw();
}

let bizName = null;
/**
 * A printable list (new window): for a pickup, where each piece is now (so the driver can find it);
 * for a loft's delivery, what is due back, to check off as it arrives.
 */
async function printList(title, list, mode, win = window.open('', '_blank')) {
  if (!win) { toastMessage('Allow pop-ups to print the list', 'red'); return; }
  if (bizName === null) bizName = (await get(ref(database, 'settings/businessProfile/name')).catch(() => null))?.val() || '';
  const rows = list.slice().sort((a, b) => names(a).boat.localeCompare(names(b).boat) || pieceName(a).localeCompare(pieceName(b)));
  const day = fmtDay(today());
  const cell = v => `<td>${esc(v || '—')}</td>`;
  win.document.open();
  win.document.write(`<!doctype html><html lang="en"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1">
<title>${esc(title)}</title>
<style>
  body { font: 13px/1.45 'Public Sans', 'Segoe UI', Arial, sans-serif; color: #1b2733; margin: 0; background: #eef2f5; }
  .bar { position: sticky; top: 0; display: flex; justify-content: flex-end; gap: 8px; padding: 10px 16px; background: #fff; border-bottom: 1px solid #d9e1e8; }
  .bar button { font: 600 13px inherit; font-family: inherit; padding: 8px 16px; border-radius: 8px; border: 1px solid #1f7a78; background: #1f7a78; color: #fff; cursor: pointer; }
  .page { max-width: 8.5in; margin: 16px auto; padding: .5in .55in; background: #fff; }
  h1 { font-size: 19px; margin: 0; } .sub { color: #5a6b7b; margin: 2px 0 16px; }
  table { width: 100%; border-collapse: collapse; } th { text-align: left; font-size: 10.5px; letter-spacing: .04em; text-transform: uppercase; color: #5a6b7b; border-bottom: 1px solid #1b2733; padding: 5px 6px; }
  td { padding: 7px 6px; border-bottom: 1px solid #d9e1e8; vertical-align: top; } .box { width: 18px; height: 18px; border: 1.5px solid #1b2733; border-radius: 3px; }
  .sign { display: grid; grid-template-columns: 2fr 1fr; gap: 28px; margin-top: 40px; } .sign div { border-top: 1px solid #1b2733; padding-top: 4px; color: #5a6b7b; font-size: 11px; }
  @media print { body { background: #fff; } .bar { display: none; } .page { margin: 0; padding: 0; max-width: none; } @page { margin: .5in; } }
</style></head><body>
<div class="bar"><button type="button" onclick="window.print()">Print</button></div>
<div class="page">
  <h1>${esc(title)}</h1>
  <div class="sub">${esc([bizName, day, `${rows.length} piece${rows.length === 1 ? '' : 's'}`].filter(Boolean).join(' · '))}</div>
  <table><thead><tr><th></th><th>Boat</th><th>Owner</th><th>Piece</th><th>${mode === 'pickup' ? 'Pick up from' : 'Due back'}</th><th>WO</th></tr></thead>
  <tbody>${rows.map(p => { const n = names(p); return `<tr><td><div class="box"></div></td>${cell(n.boat)}${cell(n.customer)}${cell(pieceName(p))}${cell(mode === 'pickup' ? p.Location : fmtDay(p.Due_Back))}${cell(p.WO)}</tr>`; }).join('')}</tbody></table>
  <div class="sign"><div>${mode === 'pickup' ? 'Picked up by' : 'Checked in by'}</div><div>Date</div></div>
</div></body></html>`);
  win.document.close();
}

/** A Dashboard link: open on Overdue only (or not), with the other filters cleared. */
export function setCanvasFilter({ overdue = false } = {}) {
  group = 'all';
  loft = '';
  overdueOnly = overdue;
  const s = el('canvas-search');
  if (s) s.value = '';
}

/** Show the board: reads names from the device copy and listens to the board's pieces while shown. */
export async function renderCanvasView() {
  if (!el('canvas-view')) return;
  const customers = await idbCursorAll('customerData').catch(() => []);
  customersById = new Map(customers.filter(Boolean).map(c => [c.Customer_ID, c]));
  if (!stopWatch) {
    let first = true; // the pieces on opening: drawn without the glow that marks someone else's change
    stopWatch = watchBoard(list => {
      if (!viewShown()) { stopWatch?.(); stopWatch = null; return; } // the board left the screen: stop listening
      pieces = list;
      const ids = new Set(list.map(p => p.id));
      [...selected].forEach(id => { if (!ids.has(id)) selected.delete(id); });
      if (first) { first = false; draw(); return; }
      if (ctl.busy() || el('cvb-batch')?.contains(document.activeElement)) { pending = true; return; }
      applyLive(draw);
    }, err => console.error('Canvas board: listen failed', err));
  }
  draw();
}

el('canvas-search')?.addEventListener('input', () => draw());
// A form closed or the batch bar lost focus: show what arrived meanwhile.
document.addEventListener('focusout', () => setTimeout(() => {
  if (pending && viewShown() && !ctl.busy() && !el('cvb-batch')?.contains(document.activeElement)) { pending = false; applyLive(draw); }
}, 300));
